/**
 * Elite Fade Designs - Header Component
 * Desktop: classic top nav
 * Mobile (≤768px): hamburger triggers iOS-style slide-in drawer
 *
 * IMPORTANT: The drawer + backdrop are appended to `document.body` (not inside `.header`)
 * because `.header` has `backdrop-filter` which creates a containing block for
 * `position: fixed` descendants — would trap the drawer inside the 58px header bar.
 *
 * The "Sign In" affordance points to /signin (customer Cognito login).
 * When a customer is already signed in, it flips to "My Account" -> /dashboard.
 */

import { t, getCurrentLanguage, setLanguage } from '@i18n/index.js';
import { getSession as getCustomerSession, signOut as customerSignOut } from '@services/customer-auth.service.js';
import './Header.css';

const NAV_LINKS = [
  { path: '/',         key: 'nav.home' },
  { path: '/services', key: 'nav.services' },
  { path: '/barbers',  key: 'nav.barbers' },
  { path: '/gallery',  key: 'nav.gallery' },
  { path: '/about',    key: 'nav.about' },
  { path: '/contact',  key: 'nav.contact' },
];

// Persist drawer open state across re-renders (route/lang change)
let drawerOpen = false;

export function initHeader(options = {}) {
  const { dark = false } = options;

  const root = document.getElementById('header-root');
  if (!root) {
    console.error('[Header] #header-root not found in DOM');
    return { render: () => {}, element: null };
  }

  // 1) Create header bar (top nav)
  const header = document.createElement('header');
  header.className = `header${dark ? ' header--dark' : ''}`;
  header.id = 'site-header';
  root.appendChild(header);

  // 2) Create drawer container OUTSIDE .header (appended to body)
  let drawerWrap = document.getElementById('header-drawer-wrap');
  if (!drawerWrap) {
    drawerWrap = document.createElement('div');
    drawerWrap.id = 'header-drawer-wrap';
    document.body.appendChild(drawerWrap);
  }

  renderHeader(header, drawerWrap);
  setupScrollBehavior(header);
  window.addEventListener('popstate', () => renderHeader(header, drawerWrap));

  return {
    render: () => renderHeader(header, drawerWrap),
    element: header,
  };
}

function renderHeader(header, drawerWrap) {
  const lang = getCurrentLanguage();
  const currentPath = window.location.pathname;
  const session = safeGetSession();
  const isAuthed = !!session;

  // Auth label + destination depend on whether we have a Cognito session
  const authLabel = isAuthed ? t('nav.myAccount') : t('nav.signIn');
  const authHref  = isAuthed ? '/dashboard' : '/signin';

  // ── Top bar ──
  header.innerHTML = `
    <div class="header__inner">
      <a href="/" class="header__brand" data-link>
        <img src="/assets/images/logo.png" alt="Elite Fade Designs"
             class="header__logo" width="36" height="36" />
        <span class="header__brand-name">Elite Fade</span>
      </a>

      <nav class="header__nav" aria-label="Main navigation">
        ${NAV_LINKS.map(link => `
          <a href="${link.path}"
             class="header__nav-link${currentPath === link.path ? ' header__nav-link--active' : ''}"
             data-link>${t(link.key)}</a>
        `).join('')}
      </nav>

      <div class="header__actions">
        <div class="header__lang-toggle" role="group" aria-label="Language">
          <button type="button"
                  class="header__lang-opt${lang === 'en' ? ' is-active' : ''}"
                  data-lang="en">EN</button>
          <button type="button"
                  class="header__lang-opt${lang === 'es' ? ' is-active' : ''}"
                  data-lang="es">ES</button>
        </div>
        <a href="${authHref}" class="header__signin-btn" data-link>${escapeHtml(authLabel)}</a>
        <a href="/booking" class="header__book-btn" data-link>${t('nav.bookNow')}</a>
        <button class="header__hamburger" aria-label="Open menu" id="hamburger" aria-expanded="false">
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>
  `;

  // ── Drawer ──
  drawerWrap.innerHTML = `
    <div class="header__drawer-backdrop" id="drawer-backdrop" aria-hidden="true"></div>

    <aside class="header__drawer" id="header-drawer" aria-hidden="true" aria-label="Mobile menu">

      <div class="header__drawer-head">
        <div class="header__drawer-brand">
          <img src="/assets/images/logo.png" alt="" class="header__drawer-logo" width="32" height="32" />
          <span class="header__drawer-brand-name">Elite Fade</span>
        </div>
        <button class="header__drawer-close" id="drawer-close" aria-label="Close menu" type="button">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" width="20" height="20">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <nav class="header__drawer-nav" aria-label="Mobile navigation">
        ${NAV_LINKS.map(link => `
          <a href="${link.path}"
             class="header__drawer-link${currentPath === link.path ? ' is-active' : ''}"
             data-link>
            <span class="header__drawer-link-text">${t(link.key)}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
                 stroke-linecap="round" stroke-linejoin="round" width="16" height="16"
                 class="header__drawer-chevron">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </a>
        `).join('')}
      </nav>

      <div class="header__drawer-divider"></div>

      <a href="${authHref}" class="header__drawer-row" data-link>
        <span class="header__drawer-row-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
               stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </span>
        <span class="header__drawer-row-text">${escapeHtml(authLabel)}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
             stroke-linecap="round" stroke-linejoin="round" width="16" height="16"
             class="header__drawer-chevron">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </a>

      ${isAuthed ? `
        <button class="header__drawer-row" id="drawer-signout" type="button">
          <span class="header__drawer-row-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
                 stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </span>
          <span class="header__drawer-row-text">${escapeHtml(t('nav.signOut'))}</span>
        </button>
      ` : ''}

      <div class="header__drawer-bottom">
        <div class="header__drawer-langs" role="group" aria-label="Language">
          <button class="header__drawer-lang ${lang === 'en' ? 'is-active' : ''}"
                  data-set-lang="en" type="button">EN</button>
          <button class="header__drawer-lang ${lang === 'es' ? 'is-active' : ''}"
                  data-set-lang="es" type="button">ES</button>
        </div>
        <a href="/booking" class="header__drawer-cta" data-link>${t('nav.bookNow')}</a>
      </div>

    </aside>
  `;

  setupEvents(header, drawerWrap);

  if (drawerOpen) applyDrawerState(header, drawerWrap, true);
}

/**
 * Wrap getSession() in try/catch so a malformed cached session never blocks
 * header rendering. Returns null on any error.
 */
function safeGetSession() {
  try {
    const s = getCustomerSession();
    // getSession() may return a Promise when token expired and refresh kicks in;
    // for synchronous header rendering we treat that as "not yet authed".
    if (s && typeof s.then === 'function') return null;
    return s;
  } catch (_) {
    return null;
  }
}

function applyDrawerState(header, drawerWrap, open) {
  const hamburger = header.querySelector('#hamburger');
  const drawer = drawerWrap.querySelector('#header-drawer');
  const backdrop = drawerWrap.querySelector('#drawer-backdrop');

  drawerOpen = open;

  hamburger?.setAttribute('aria-expanded', String(open));
  hamburger?.classList.toggle('header__hamburger--open', open);
  drawer?.classList.toggle('header__drawer--open', open);
  drawer?.setAttribute('aria-hidden', String(!open));
  backdrop?.classList.toggle('header__drawer-backdrop--open', open);
  backdrop?.setAttribute('aria-hidden', String(!open));

  document.body.style.overflow = open ? 'hidden' : '';
}

function setupEvents(header, drawerWrap) {
  // Header language toggle
  header.querySelectorAll('.header__lang-opt').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const next = btn.dataset.lang;
      if (next && next !== getCurrentLanguage()) setLanguage(next);
    });
  });

  // Drawer open/close
  const hamburger = header.querySelector('#hamburger');
  hamburger?.addEventListener('click', () => {
    applyDrawerState(header, drawerWrap, !drawerOpen);
  });
  drawerWrap.querySelector('#drawer-close')?.addEventListener('click', () => {
    applyDrawerState(header, drawerWrap, false);
  });
  drawerWrap.querySelector('#drawer-backdrop')?.addEventListener('click', () => {
    applyDrawerState(header, drawerWrap, false);
  });

  // Drawer language buttons
  drawerWrap.querySelectorAll('[data-set-lang]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      setLanguage(btn.dataset.setLang);
    });
  });

  // Sign-out from drawer
  drawerWrap.querySelector('#drawer-signout')?.addEventListener('click', () => {
    try { customerSignOut(); } catch (_) {}
    applyDrawerState(header, drawerWrap, false);
    history.pushState(null, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  });

  // SPA navigation — closes drawer on link click
  const allLinks = [
    ...header.querySelectorAll('[data-link]'),
    ...drawerWrap.querySelectorAll('[data-link]'),
  ];
  allLinks.forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      const href = link.getAttribute('href');
      if (href && href !== window.location.pathname) {
        history.pushState(null, '', href);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
      applyDrawerState(header, drawerWrap, false);
    });
  });
}

function setupScrollBehavior(header) {
  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        header.classList.toggle('header--scrolled', window.scrollY > 40);
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}
